class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.openFromCartBounce();
    this.watchForRestoredPage();
    this.watchForCartUpdates();

    /*
     * Opening a disclosure in the drawer — the order note — moves focus to its
     * field, so the reader can type straight away instead of tabbing to it.
     *
     * Delegated from the host, which survives renderContents replacing the
     * inner HTML. Capture phase because `toggle` does not bubble.
     */
    this.addEventListener(
      'toggle',
      (event) => {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement) || !details.open) return;
        const field = details.querySelector('textarea, input:not([type="hidden"])');
        if (field) field.focus();
      },
      true
    );
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /*
   * sections/aw-cart-redirect.liquid bounces /cart to the previous page and
   * marks the arrival with ?cart-open=1. Open the drawer here and take the
   * flag back out of the URL, so the address is the one the visitor expects
   * and a reload does not re-open the drawer on its own.
   */
  openFromCartBounce() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('cart-open') !== '1') return;

    url.searchParams.delete('cart-open');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);

    this.open();
  }

  /*
   * The drawer is server-rendered into every page, so a page restored by the
   * back/forward cache comes back showing the cart it had when that page was
   * built — clear the cart, go back, and the deleted items are there again,
   * with a stale count in the header to match.
   *
   * Only back/forward navigations are watched. `persisted` catches a bfcache
   * restore; the navigation type catches an HTTP-cached back navigation, where
   * persisted is false but the markup is just as old. A normal load does
   * neither, so nothing here costs anything in the common path.
   *
   * A `pageshow` listener does not disqualify a page from bfcache. `unload`
   * would, so that is the thing not to reach for here later.
   */
  watchForRestoredPage() {
    window.addEventListener('pageshow', (event) => {
      const entry = performance.getEntriesByType('navigation')[0];
      if (!event.persisted && (!entry || entry.type !== 'back_forward')) return;
      this.refreshIfStale();
    });
  }

  /*
   * A cart change made anywhere other than this drawer leaves it stale. The
   * cart page is the case that shows: cart.js:122-145 re-renders only
   * main-cart-items, main-cart-footer, cart-icon-bubble and the live region —
   * never cart-drawer — but cart.js:181 still toggles is-empty onto this host.
   * The empty-state markup is Liquid-gated (cart-drawer.liquid:39), so on a
   * page loaded while the cart had items it does not exist at all: the class
   * hides the header and the line items and reveals nothing underneath. The
   * result is a blank drawer with no Continue shopping button. A partial
   * delete leaves the same staleness, just less visibly — the drawer goes on
   * listing items that are gone.
   *
   * The comparison is against what this drawer is currently showing, not
   * against the event's `source`, because the drawer's own items element
   * extends CartItems and publishes the same source. Comparing rendered state
   * also makes a loop impossible: after a refresh the drawer agrees with the
   * cart, so the next event is a no-op.
   */
  watchForCartUpdates() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const cart = event && event.cartData;
      if (!cart) return;

      const cartIsEmpty = cart.item_count === 0;
      const showingEmpty = !!this.querySelector('.drawer__inner-empty');
      const shownLines = this.querySelectorAll('.cart-item').length;
      const actualLines = cart.items ? cart.items.length : shownLines;

      if (cartIsEmpty === showingEmpty && shownLines === actualLines) return;

      this.refreshFromServer();
    });
  }

  /*
   * /cart.js first, because it is a few hundred bytes against the drawer
   * section's ~6KB and most back-navigations happen with an unchanged cart.
   * When the count matches what this markup was rendered against there is
   * nothing to do — and not replacing the DOM is what keeps an open drawer's
   * scroll position and focus intact in that case.
   */
  refreshIfStale() {
    fetch(`${routes.cart_url}.js`)
      .then((response) => {
        if (!response.ok) return null;
        return response.json();
      })
      .then((cart) => {
        if (!cart) return;
        if (String(cart.item_count) === this.dataset.cartCount) return;
        return this.refreshFromServer();
      });
  }

  refreshFromServer() {
    const ids = this.getSectionsToRender().map((section) => section.id);

    return fetch(`${routes.cart_url}?sections=${ids.join(',')}`)
      .then((response) => {
        if (!response.ok) return null;
        return response.json();
      })
      .then((sections) => {
        if (!sections) return;

        const fresh = this.getSectionDOM(sections['cart-drawer'], 'cart-drawer');
        if (fresh) {
          // The class carries is-empty and it sits on the host, so replacing
          // only the inner HTML would restore the items and leave the empty
          // state wrong.
          this.className = fresh.className;
          this.dataset.cartCount = fresh.dataset.cartCount;
          this.innerHTML = fresh.innerHTML;
          this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        }

        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && sections['cart-icon-bubble']) {
          bubble.innerHTML = this.getSectionInnerHTML(sections['cart-icon-bubble']);
        }
      });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    /*
     * is-empty also sits on this host element, and the empty-state rules key
     * off it: cart-drawer.is-empty hides .drawer__header and collapses
     * .drawer__inner to a single centred row. Replacing the inner HTML alone
     * leaves those active, so a cart that was empty at page load renders
     * without its header or items after an add.
     */
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
